import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    customerItems: [],
    customer: {},
    customerPayments: [],
    fetchStatus: "idle",
    fetchByIdStatus: "idle",
    fetchCustomerOrderStatus: "idle",
    fetchCustomerPaymentStatus: "idle",
    updatePaymentStatus: "idle",
    createStatus: "idle",
    updateStatus: "idle",
    deleteStatus: "idle",
    pagination: {},
    dateRange: {},
    error: null,
};

//Fetch customer
export const fetchCustomers = createAsyncThunk('employee/fetchCustomers',
    async ({scopeId, roleSign, page, limit}) => {
        try {
            const params = new URLSearchParams();
            if (page) params.append("page", page);
            if (limit) params.append("limit", limit);

            const queryString = params.toString();
            const fullParams = queryString ? `&${queryString}` : '';

            const response = await api.get(`/${roleSign}/customers?id=${scopeId}${fullParams}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch customer by ID
export const fetchCustomerById = createAsyncThunk('customer/fetchCustomerById',
    async ({id, roleSign}) => {
        try {
            const response = await api.get(`/${roleSign}/customers/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch customer order
export const fetchCustomerOrder = createAsyncThunk('customer/fetchCustomerOrder',
    async ({scopeId, roleSign, customerId, queryString}) => {
        try {
            const response = await api.get(`/${roleSign}/customers/customerOrderDetail?id=${scopeId}&user=${customerId}&${queryString}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Create a new customer
export const createCustomer = createAsyncThunk('customer/createCustomer',
    async ({scopeId, roleSign, data}) => {
        try {
            const response = await api.post(`/${roleSign}/customers?id=${scopeId}`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Update customer
export const updateCustomer = createAsyncThunk('customer/updateCustomer',
    async ({id, roleSign, data}) => {
        try {
            const response = await api.patch(`/${roleSign}/customers/${id}`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Update customer payment status
export const updateCustomerPaymentStatus = createAsyncThunk('customer/updateCustomerPaymentStatus',
    async ({roleSign, scopeId, customer, data}) => {
        try {
            const response = await api.patch(`/${roleSign}/customers/updatePayment?id=${scopeId}&user=${customer}`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Delete employee
export const deleteCustomer = createAsyncThunk('customer/deleteCustomer',
    async ({roleSign, id}) => {
        try {
            const response = await api.delete(`/${roleSign}/customers/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch customer payments
export const fetchCustomerPayments = createAsyncThunk('customer/fetchCustomerPayments',
    async ({scopeId, roleSign, customer, formData = {start: '', end: ''}} = {}) => {
        try {
            const {start, end} = formData;
            const params = new URLSearchParams();

            if (start) params.append("start", start);
            if (end) params.append("end", end);

            const queryString = params.toString();
            const fullParams = queryString ? `&${queryString}` : '';

            const response = await api.get(`/${roleSign}/customers/paymentHistory?id=${scopeId}&user=${customer}${fullParams}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

const customerSlice = createSlice({
    name: "customer",
    initialState,
    reducers: {
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetUpdatePaymentStatus(state) {
            state.updatePaymentStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Customer
            .addCase(fetchCustomers.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchCustomers.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchCustomers.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Create customer
            .addCase(createCustomer.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createCustomer.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createCustomer.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch customer by ID
            .addCase(fetchCustomerById.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchCustomerById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchCustomerById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Customer order
            .addCase(fetchCustomerOrder.pending, (state) => {
                state.fetchCustomerOrderStatus = 'loading';
            })
            .addCase(fetchCustomerOrder.fulfilled, (state, action) => {
                const {
                    fetchCustomerOrderStatus,
                    error
                } = handleResponseStatus(action.payload, 'fetchCustomerOrderStatus');
                const {data, customer, totalFinalPrice, totalOrders} = action.payload.data;
                if (fetchCustomerOrderStatus === "success") {
                    state.customerItems = updateIdField(data); // update your order list
                    state.customer = {
                        ...customer,
                        totalOrders,
                        totalFinalPrice
                    };
                }
                state.fetchCustomerOrderStatus = fetchCustomerOrderStatus;
                state.error = error;
            })
            .addCase(fetchCustomerOrder.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Update customer
            .addCase(updateCustomer.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateCustomer.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateCustomer.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // Update customer payment status
            .addCase(updateCustomerPaymentStatus.pending, (state) => {
                state.updatePaymentStatus = 'loading';
            })
            .addCase(updateCustomerPaymentStatus.fulfilled, (state, action) => {
                const {updatePaymentStatus, error} = handleResponseStatus(action.payload, 'updatePaymentStatus');
                state.updatePaymentStatus = updatePaymentStatus;
                state.error = error;
            })
            .addCase(updateCustomerPaymentStatus.rejected, (state, action) => {
                state.updatePaymentStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete customer
            .addCase(deleteCustomer.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteCustomer.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                const id = action.meta.arg.id;
                if (deleteStatus === "success") {
                    state.items = state.items.filter(item => item.id !== id);
                }
                state.deleteStatus = deleteStatus;
                state.error = error;
            })
            .addCase(deleteCustomer.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Customer payments
            .addCase(fetchCustomerPayments.pending, (state) => {
                state.fetchCustomerPaymentStatus = 'loading';
            })
            .addCase(fetchCustomerPayments.fulfilled, (state, action) => {
                const {
                    fetchCustomerPaymentStatus,
                    error
                } = handleResponseStatus(action.payload, 'fetchCustomerPaymentStatus');
                const {data, dateRange} = action.payload.data;
                state.customerPayments = fetchCustomerPaymentStatus === "success" ? updateIdField(data) : state.customerPayments;
                state.fetchCustomerPaymentStatus = fetchCustomerPaymentStatus;
                state.dateRange = dateRange;
                state.error = error;
            })
            .addCase(fetchCustomerPayments.rejected, (state, action) => {
                state.fetchCustomerPaymentStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetUpdateStatus,
    resetDeleteStatus,
    resetCreateStatus,
    resetUpdatePaymentStatus,
} = customerSlice.actions;

export default customerSlice.reducer;



